import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Loader, X, Home, Briefcase, Clock, Star, Navigation } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = 'fq_recent_locations';
const MAX_RECENT = 5;

const LocationSearchInput = ({
  onSelect,
  placeholder = 'Suburb, postcode or city...',
  initialValue = '',
  autoFocus = false,
  className = '',
  variant = 'default',
  testIdPrefix = 'location',
}) => {
  const { user } = useAuth();
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [recentLocations, setRecentLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showQuickPanel, setShowQuickPanel] = useState(false);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Load recent locations from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecentLocations(Array.isArray(stored) ? stored.slice(0, MAX_RECENT) : []);
    } catch { setRecentLocations([]); }
  }, []);

  // Fetch saved addresses when user is logged in
  const fetchSavedAddresses = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/addresses`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedAddresses(data.addresses || []);
      }
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchSavedAddresses(); }, [fetchSavedAddresses]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setShowQuickPanel(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Save a location to recent history
  const saveToRecent = (locObj) => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const filtered = stored.filter(
        (l) => l.displayName !== locObj.displayName
      );
      const updated = [locObj, ...filtered].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      setRecentLocations(updated);
    } catch { /* ignore */ }
  };

  const formatSuggestion = (item) => {
    const a = item.address || {};
    const suburb = a.suburb || a.neighbourhood || a.quarter;
    const city = a.city || a.town || a.village || a.county;
    const state = a.state_code || a.state;
    const country = a.country;
    const postcode = a.postcode;
    let line1 = suburb || city || item.display_name.split(',')[0];
    let line2Parts = [];
    if (suburb && city && suburb !== city) line2Parts.push(city);
    if (state) line2Parts.push(state);
    if (postcode) line2Parts.push(postcode);
    if (country) line2Parts.push(country);
    return { line1, line2: line2Parts.join(', '), type: item.type || item.class };
  };

  const buildLocationObj = (item) => {
    const a = item.address || {};
    const suburb = a.suburb || a.neighbourhood || a.quarter;
    const city = a.city || a.town || a.village || a.county || suburb || '';
    const country = a.country || '';
    const postcode = a.postcode;
    const state = a.state_code || a.state || '';
    let displayName = suburb ? `${suburb}, ${city}` : city;
    if (postcode && !suburb && state) displayName = `${city} ${state} ${postcode}`;
    else if (postcode && !suburb) displayName = `${city} ${postcode}`;
    return {
      city: suburb || city,
      suburb: suburb || null,
      postcode: postcode || null,
      country, state,
      displayName: displayName.trim(),
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      fullAddress: item.display_name,
    };
  };

  const search = async (val) => {
    if (!val || val.length < 2) { setSuggestions([]); setOpen(false); setShowQuickPanel(false); return; }
    setLoading(true);
    setShowQuickPanel(false);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=6&featuretype=settlement`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'FoodQuestApp/1.0' } }
      );
      const data = await res.json();
      setSuggestions(data || []);
      setOpen(data.length > 0);
    } catch { setSuggestions([]); }
    setLoading(false);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val.length < 2) {
      setSuggestions([]);
      setOpen(false);
      if (val.length === 0) setShowQuickPanel(true);
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => search(val), 350);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setOpen(true);
    } else if (!query || query.length < 2) {
      setShowQuickPanel(true);
    }
  };

  const handleSelect = (item) => {
    const locObj = buildLocationObj(item);
    const fmt = formatSuggestion(item);
    setQuery(fmt.line1 + (fmt.line2 ? ', ' + fmt.line2.split(',')[0] : ''));
    setSuggestions([]);
    setOpen(false);
    setShowQuickPanel(false);
    saveToRecent(locObj);
    onSelect(locObj);
  };

  const handleSelectSaved = (addr) => {
    const locObj = {
      city: addr.display_name.split(',')[0],
      displayName: addr.display_name,
      lat: addr.lat,
      lng: addr.lng,
      fullAddress: addr.full_address || addr.display_name,
    };
    setQuery(addr.display_name);
    setOpen(false);
    setShowQuickPanel(false);
    saveToRecent(locObj);
    onSelect(locObj);
  };

  const handleSelectRecent = (loc) => {
    setQuery(loc.displayName);
    setOpen(false);
    setShowQuickPanel(false);
    onSelect(loc);
  };

  const clear = () => {
    setQuery('');
    setSuggestions([]);
    setOpen(false);
    setShowQuickPanel(true);
  };

  const getLabelIcon = (label) => {
    const l = (label || '').toLowerCase();
    if (l === 'home') return Home;
    if (l === 'office' || l === 'work') return Briefcase;
    return Star;
  };

  const hasQuickItems = savedAddresses.length > 0 || recentLocations.length > 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative flex items-center">
        {variant === 'default' && (
          <MapPin className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
        <input
          data-testid={`${testIdPrefix}-search-input`}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={
            variant === 'minimal'
              ? 'w-full bg-transparent text-white text-sm outline-none placeholder:text-white/45 min-w-0'
              : 'w-full pl-9 pr-9 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition-all bg-white'
          }
        />
        {variant === 'default' && (
          <div className="absolute right-3">
            {loading ? (
              <Loader className="w-4 h-4 text-gray-400 animate-spin" />
            ) : query ? (
              <button onClick={clear} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        )}
        {variant === 'minimal' && loading && (
          <Loader className="w-3.5 h-3.5 text-white/40 animate-spin flex-shrink-0 ml-1" />
        )}
      </div>

      {/* Quick Panel: Saved Addresses + Recent Locations */}
      {showQuickPanel && hasQuickItems && (
        <div
          data-testid="location-quick-panel"
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-2xl z-[10001] overflow-hidden min-w-[280px]"
        >
          {/* Saved Addresses */}
          {savedAddresses.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Saved Addresses</p>
              {savedAddresses.map((addr) => {
                const Icon = getLabelIcon(addr.label);
                return (
                  <button
                    key={addr.address_id}
                    data-testid={`location-saved-${addr.address_id}`}
                    onClick={() => handleSelectSaved(addr)}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#FFF5F0] flex items-center gap-3 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#FFF5F0] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#FF6B35]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{addr.label}</p>
                      <p className="text-xs text-gray-500 truncate">{addr.display_name}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Recent Locations */}
          {recentLocations.length > 0 && (
            <div className={savedAddresses.length > 0 ? 'border-t border-gray-100' : ''}>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Recent</p>
              {recentLocations.map((loc, i) => (
                <button
                  key={`recent-${i}`}
                  data-testid={`location-recent-${i}`}
                  onClick={() => handleSelectRecent(loc)}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#FFF5F0] flex items-center gap-3 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{loc.displayName || loc.city}</p>
                    {loc.country && <p className="text-xs text-gray-500 truncate">{loc.country}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Use GPS hint */}
          <div className="border-t border-gray-100">
            <button
              data-testid="location-use-gps"
              onClick={() => {
                setShowQuickPanel(false);
                if (navigator.geolocation) {
                  setLoading(true);
                  navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                      try {
                        const res = await fetch(
                          `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,
                          { headers: { 'Accept-Language': 'en' } }
                        );
                        const data = await res.json();
                        const city = data.address?.city || data.address?.town || data.address?.village || 'Your City';
                        const country = data.address?.country || '';
                        const locObj = {
                          city,
                          country,
                          displayName: `${city}${country ? ', ' + country : ''}`,
                          lat: pos.coords.latitude,
                          lng: pos.coords.longitude,
                        };
                        setQuery(locObj.displayName);
                        saveToRecent(locObj);
                        onSelect(locObj);
                      } catch { /* ignore */ }
                      setLoading(false);
                    },
                    () => setLoading(false),
                    { timeout: 8000 }
                  );
                }
              }}
              className="w-full px-4 py-2.5 text-left hover:bg-[#FFF5F0] flex items-center gap-3 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-sm font-medium text-blue-600">Use current location</p>
            </button>
          </div>
        </div>
      )}

      {/* Map search suggestions dropdown */}
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-2xl z-[10001] overflow-hidden min-w-[280px]">
          {suggestions.map((item, i) => {
            const fmt = formatSuggestion(item);
            return (
              <button
                key={i}
                data-testid={`${testIdPrefix}-suggestion-${i}`}
                onClick={() => handleSelect(item)}
                className="w-full px-4 py-3 text-left hover:bg-[#FFF5F0] border-b border-gray-50 last:border-0 flex items-start gap-3 transition-colors"
              >
                <MapPin className="w-4 h-4 text-[#FF6B35] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{fmt.line1}</p>
                  {fmt.line2 && <p className="text-xs text-gray-500 truncate">{fmt.line2}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LocationSearchInput;
